// ===========================================
// Family Upcoming Schedule Component
// ===========================================
// Displays all students' lessons for the next 14 days in chronological order
// Uses CalendarEvent data from the parent-accessible /calendar/my-events endpoint

import { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Event as EventIcon,
  AccessTime as TimeIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format, isToday, isTomorrow, addDays, startOfDay } from 'date-fns';
import { toLocalDateString } from '../../utils/dateUtils';
import { CalendarEvent } from '../../api/hybridBooking.api';
import { StudentSummary } from '../../api/user.api';

// ===========================================
// TYPES
// ===========================================

interface ScheduleItem {
  id: string;
  type: 'lesson' | 'booking' | 'placeholder';
  date: Date;
  startTime: Date;
  endTime: Date;
  title: string;
  teacherName?: string;
  locationName?: string;
  studentName?: string;
  isHybrid?: boolean;
  isIndividual?: boolean;
  teacherColor?: string;
  instrumentColor?: string;
  isCancelled?: boolean;
}

interface FamilyUpcomingScheduleProps {
  students: StudentSummary[];
  calendarEvents: CalendarEvent[];
  daysAhead?: number;
  isLoading: boolean;
  maxItems?: number;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function formatDateLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'EEEE, d MMM');
}

function formatTimeRange(start: Date, end: Date): string {
  return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
}


// ===========================================
// COMPONENT
// ===========================================

export default function FamilyUpcomingSchedule({
  students: _students,
  calendarEvents,
  daysAhead = 14,
  isLoading,
  maxItems = 20,
}: FamilyUpcomingScheduleProps) {
  // _students prop kept for interface compatibility but not used in display
  void _students;

  // Filter and transform calendar events into schedule items
  const scheduleItems = useMemo(() => {
    const today = startOfDay(new Date());
    const endDate = addDays(today, daysAhead);

    const items: ScheduleItem[] = calendarEvents
      .filter((event) => {
        // Filter to events within our date range
        const eventDate = startOfDay(event.start);
        return eventDate >= today && eventDate < endDate;
      })
      .filter((event) => {
        // Exclude placeholder events (those are for booking, not display)
        // School events (#256) are not lessons; they have their own surfaces.
        return event.resource.type !== 'HYBRID_PLACEHOLDER' && event.resource.type !== 'EVENT';
      })
      .map((event) => {
        const isHybrid = event.resource.type === 'HYBRID_GROUP' ||
          event.resource.type === 'HYBRID_INDIVIDUAL';
        const isIndividual = event.resource.type === 'HYBRID_INDIVIDUAL' ||
          event.resource.type === 'INDIVIDUAL';

        return {
          id: event.id,
          type: event.resource.isBooking ? 'booking' : 'lesson',
          date: startOfDay(event.start),
          startTime: event.start,
          endTime: event.end,
          title: event.title,
          teacherName: event.resource.teacherName,
          locationName: event.resource.locationName,
          studentName: event.resource.studentName,
          isHybrid,
          isIndividual,
          teacherColor: event.resource.teacherColor,
          instrumentColor: event.resource.instrumentColor,
          isCancelled: event.resource.isCancelled,
        } as ScheduleItem;
      })
      .sort((a, b) => {
        // Sort by date, then by time
        const dateCompare = a.date.getTime() - b.date.getTime();
        if (dateCompare !== 0) return dateCompare;
        return a.startTime.getTime() - b.startTime.getTime();
      })
      .slice(0, maxItems);

    return items;
  }, [calendarEvents, daysAhead, maxItems]);

  // Group items by date for display
  const groupedItems = useMemo(() => {
    const groups: { date: Date; label: string; items: ScheduleItem[] }[] = [];
    let currentDateKey: string | null = null;

    for (const item of scheduleItems) {
      // Group by the item's LOCAL calendar day (item.date is a fake-local startOfDay). Using
      // toISOString() here would shift the key to UTC (July 20 → July 19 for AEST viewers) — a
      // latent off-by-one that toLocalDateString avoids, keeping the key aligned with the
      // displayed date for any viewer timezone.
      const dateKey = toLocalDateString(item.date);
      if (dateKey !== currentDateKey) {
        currentDateKey = dateKey;
        groups.push({
          date: item.date,
          label: formatDateLabel(item.date),
          items: [],
        });
      }
      groups[groups.length - 1].items.push(item);
    }

    return groups;
  }, [scheduleItems]);

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader title="Upcoming Schedule" />
        <CardContent>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (scheduleItems.length === 0) {
    return (
      <Card>
        <CardHeader
          title="Upcoming Schedule"
          avatar={<EventIcon color="primary" />}
        />
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No upcoming lessons in the next {daysAhead} days
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Upcoming Schedule"
        subheader={`Next ${daysAhead} days`}
        avatar={<EventIcon color="primary" />}
      />
      <CardContent sx={{ p: 0 }}>
        <List disablePadding>
          {groupedItems.map((group, groupIndex) => (
            <Box key={group.label}>
              {/* Date Header */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: isToday(group.date) ? 'primary.light' : '#FCF6E6',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color={isToday(group.date) ? 'primary.contrastText' : 'text.primary'}
                >
                  {group.label}
                </Typography>
              </Box>

              {/* Items for this date */}
              {group.items.map((item, itemIndex) => (
                  <Box key={item.id}>
                    <ListItem
                      sx={{
                        borderLeft: `4px solid ${item.instrumentColor || item.teacherColor || '#4580E4'}`,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{
                                textDecoration: item.isCancelled ? 'line-through' : 'none',
                                color: item.isCancelled ? 'text.disabled' : 'text.primary',
                              }}
                            >
                              {item.title}
                            </Typography>
                            {item.isCancelled && (
                              <Chip
                                icon={<CancelIcon />}
                                label="Cancelled"
                                size="small"
                                color="error"
                                sx={{ height: 20 }}
                              />
                            )}
                            {item.isIndividual && (
                              <Chip
                                label="Individual"
                                size="small"
                                color="secondary"
                                sx={{ height: 20 }}
                              />
                            )}
                            {item.isHybrid && !item.isIndividual && (
                              <Chip
                                label="Hybrid"
                                size="small"
                                color="default"
                                sx={{ height: 20 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textDecoration: item.isCancelled ? 'line-through' : 'none',
                                  opacity: item.isCancelled ? 0.6 : 1,
                                }}
                              >
                                {formatTimeRange(item.startTime, item.endTime)}
                              </Typography>
                            </Box>
                            {item.teacherName && (
                              <Chip
                                label={item.teacherName}
                                size="small"
                                sx={{
                                  height: 20,
                                  bgcolor: item.teacherColor || 'grey.300',
                                  color: item.teacherColor ? 'white' : 'text.primary',
                                  opacity: item.isCancelled ? 0.6 : 1,
                                  '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                                }}
                              />
                            )}
                            {item.locationName && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textDecoration: item.isCancelled ? 'line-through' : 'none',
                                  opacity: item.isCancelled ? 0.6 : 1,
                                }}
                              >
                                {item.locationName}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {itemIndex < group.items.length - 1 && <Divider component="li" />}
                  </Box>
                ))}


              {groupIndex < groupedItems.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
